import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Linkedin, Facebook, Link2 } from "lucide-react";
import { getUpdate, listUpdates } from "../lib/api";
import {
    chipClasses, dotClasses, formatDate, getCategory,
} from "../lib/updates";
import { sanitizeHtml } from "../lib/sanitize";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CountdownStrip from "../components/CountdownStrip";

export default function UpdatePostPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [related, setRelated] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setPost(null);
        setError(null);
        getUpdate(slug)
            .then((p) => {
                if (cancelled) return;
                setPost(p);
                document.title = `${p.title} — Cleany.dk`;
            })
            .catch((e) => {
                if (cancelled) return;
                if (e?.response?.status === 404) {
                    setError("not_found");
                } else {
                    setError("generic");
                }
            });
        return () => { cancelled = true; };
    }, [slug]);

    // Fetch related posts (same category, exclude current)
    useEffect(() => {
        if (!post) return;
        let cancelled = false;
        listUpdates({ category: post.category, limit: 4 })
            .then((list) => {
                if (cancelled) return;
                setRelated(list.filter((p) => p.slug !== post.slug).slice(0, 3));
            })
            .catch(() => { if (!cancelled) setRelated([]); });
        return () => { cancelled = true; };
    }, [post]);

    const openWaitlist = (source) =>
        window.dispatchEvent(new CustomEvent("cleany:open-waitlist", { detail: { source } }));

    if (error === "not_found") {
        return (
            <NotFoundShell onCtaClick={() => openWaitlist("updates-post-404")} />
        );
    }

    return (
        <>
            <CountdownStrip onCtaClick={() => openWaitlist("updates-post-strip")} />
            <Navbar onCtaClick={() => openWaitlist("updates-post-navbar")} />

            <main className="bg-white">
                {/* Back link */}
                <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-12">
                    <Link
                        to="/updates"
                        data-testid="post-back-link"
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700 hover:text-sky-900 transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Alle indlæg
                    </Link>
                </div>

                {!post ? (
                    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-32 text-center text-slate-500" data-testid="post-loading">
                        Indlæser…
                    </div>
                ) : (
                    <>
                        {/* Header */}
                        <header className="max-w-3xl mx-auto px-6 lg:px-10 pt-6 pb-8">
                            <CategoryChip slug={post.category} />
                            <h1 className="mt-4 font-display text-3xl md:text-5xl font-bold tracking-tight text-slate-900 leading-tight">
                                {post.title}
                            </h1>
                            <div className="mt-5 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                                <span className="inline-flex items-center gap-2">
                                    <span className="h-8 w-8 rounded-full bg-gradient-to-br from-sky-500 to-sky-700 inline-flex items-center justify-center text-white text-xs font-bold">
                                        {(post.author || "C").slice(0, 1)}
                                    </span>
                                    <span className="font-semibold text-slate-700">{post.author}</span>
                                </span>
                                <span>·</span>
                                <span>{formatDate(post.date)}</span>
                                <span>·</span>
                                <span>{post.reading_time} min læsning</span>
                            </div>
                        </header>

                        {/* Cover */}
                        {post.cover && (
                            <div className="max-w-4xl mx-auto px-6 lg:px-10">
                                <div className="rounded-3xl overflow-hidden border border-slate-200 bg-slate-50 aspect-[16/9]">
                                    <img
                                        src={post.cover}
                                        alt={post.title}
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Body */}
                        <article
                            data-testid="post-body"
                            className="max-w-3xl mx-auto px-6 lg:px-10 py-12 prose-cleany"
                            dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.html) }}
                        />

                        {/* Share row */}
                        <div className="max-w-3xl mx-auto px-6 lg:px-10 pb-12">
                            <div className="rounded-2xl border border-slate-200 bg-slate-50/60 px-6 py-5 flex flex-wrap items-center justify-between gap-4">
                                <div className="text-sm text-slate-600">
                                    Del indlægget med en kollega:
                                </div>
                                <ShareRow title={post.title} />
                            </div>
                        </div>

                        {/* CTA */}
                        <div className="max-w-3xl mx-auto px-6 lg:px-10 pb-16">
                            <div className="rounded-3xl bg-gradient-to-br from-sky-600 to-sky-800 text-white p-8 md:p-10 relative overflow-hidden">
                                <div className="absolute inset-0 bg-grid opacity-15" />
                                <div className="relative">
                                    <div className="text-[11px] font-bold uppercase tracking-wider text-sky-200">
                                        Klar til at modernisere driften?
                                    </div>
                                    <h3 className="mt-2 font-display text-2xl md:text-3xl font-bold">
                                        Tilmeld dig launch — bliv første kunde.
                                    </h3>
                                    <p className="mt-3 text-sky-100 max-w-md text-sm md:text-base">
                                        Cleany lancerer 1. oktober 2026. Tilmeld dig ventelisten og bliv én af de første kunder.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => openWaitlist("updates-post-cta")}
                                        data-testid="post-cta"
                                        className="mt-5 inline-flex items-center gap-2 rounded-full bg-white text-sky-700 hover:bg-sky-50 font-semibold px-6 h-11 shadow-lg transition-all active:scale-[0.98]"
                                    >
                                        Tilmeld launch
                                        <ArrowRight className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Related */}
                        {related.length > 0 && (
                            <section className="bg-slate-50 border-t border-slate-200 py-16">
                                <div className="max-w-5xl mx-auto px-6 lg:px-10">
                                    <h2 className="font-display text-2xl font-bold text-slate-900">
                                        Relaterede indlæg
                                    </h2>
                                    <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-5">
                                        {related.map((r) => (
                                            <Link
                                                key={r.slug}
                                                to={`/updates/${r.slug}`}
                                                onClick={() => window.scrollTo(0, 0)}
                                                data-testid={`related-${r.slug}`}
                                                className="group block rounded-2xl bg-white border border-slate-200 overflow-hidden hover:border-sky-300 hover:-translate-y-1 hover:shadow-lg transition-all"
                                            >
                                                <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                                                    {r.cover && <img src={r.cover} alt={r.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />}
                                                </div>
                                                <div className="p-4">
                                                    <CategoryChip slug={r.category} />
                                                    <h3 className="mt-2.5 font-display text-base font-bold text-slate-900 group-hover:text-sky-700 transition-colors line-clamp-2 leading-snug">
                                                        {r.title}
                                                    </h3>
                                                    <p className="mt-1.5 text-xs text-slate-500">{formatDate(r.date)} · {r.reading_time} min</p>
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            </section>
                        )}
                    </>
                )}
            </main>

            <Footer onCtaClick={() => openWaitlist("updates-post-footer")} />
        </>
    );
}

/* ---------- pieces ---------- */
function CategoryChip({ slug }) {
    const c = getCategory(slug);
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${chipClasses(c.color)}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${dotClasses(c.color)}`} />
            {c.label}
        </span>
    );
}

function ShareRow({ title }) {
    const url = typeof window !== "undefined" ? window.location.href : "";
    const enc = encodeURIComponent;
    const linkedin = `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`;
    const facebook = `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}&quote=${enc(title)}`;
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Link kopieret");
        } catch {
            toast.error("Kunne ikke kopiere link");
        }
    };
    return (
        <div className="flex items-center gap-2">
            <a href={linkedin} target="_blank" rel="noopener noreferrer" data-testid="share-linkedin" aria-label="Del på LinkedIn"
               className="h-9 w-9 rounded-full bg-white border border-slate-200 hover:border-sky-300 hover:text-sky-700 text-slate-600 inline-flex items-center justify-center transition-colors">
                <Linkedin className="h-4 w-4" strokeWidth={1.75} />
            </a>
            <a href={facebook} target="_blank" rel="noopener noreferrer" data-testid="share-facebook" aria-label="Del på Facebook"
               className="h-9 w-9 rounded-full bg-white border border-slate-200 hover:border-sky-300 hover:text-sky-700 text-slate-600 inline-flex items-center justify-center transition-colors">
                <Facebook className="h-4 w-4" strokeWidth={1.75} />
            </a>
            <button type="button" onClick={copy} data-testid="share-copy" aria-label="Kopier link"
                    className="h-9 w-9 rounded-full bg-white border border-slate-200 hover:border-sky-300 hover:text-sky-700 text-slate-600 inline-flex items-center justify-center transition-colors">
                <Link2 className="h-4 w-4" strokeWidth={1.75} />
            </button>
        </div>
    );
}

function NotFoundShell({ onCtaClick }) {
    return (
        <>
            <Navbar onCtaClick={onCtaClick} />
            <main className="bg-white">
                <div className="max-w-2xl mx-auto px-6 lg:px-10 py-32 text-center" data-testid="post-not-found">
                    <div className="text-7xl">📭</div>
                    <h1 className="mt-6 font-display text-3xl font-bold text-slate-900">
                        Indlægget findes ikke
                    </h1>
                    <p className="mt-3 text-slate-600">
                        Måske er det blevet flyttet, eller du har skrevet linket forkert.
                    </p>
                    <Link
                        to="/updates"
                        className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold px-6 h-11 transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Tilbage til alle indlæg
                    </Link>
                </div>
            </main>
            <Footer onCtaClick={onCtaClick} />
        </>
    );
}
